'use client';

/**
 * ThemeProvider - Real Theme Management
 * 
 * Manages dark/light mode across the application.
 * Persists theme preference to localStorage.
 * Toggles 'dark' class on <html> element for Tailwind dark mode.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface ThemeContextType {
  mode: 'light' | 'dark' | 'midnight';
  toggleTheme: () => void;
  setThemeMode: (mode: 'light' | 'dark' | 'midnight') => void;
  setHighContrast: (enabled: boolean) => void;
  setFontSize: (size: 'normal' | 'large' | 'larger') => void;
  setReduceMotion: (enabled: boolean) => void;
  setModuleColor: (module: string, color: string) => void;
  resetModuleColors: () => void;
  isHighContrast: boolean;
  fontSize: 'normal' | 'large' | 'larger';
  reduceMotion: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'pi-app-theme';
const SETTINGS_STORAGE_KEY = 'pi-app-theme-settings';

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark' | 'midnight'>('light');
  const [isHighContrast, setIsHighContrast] = useState(false);
  const [fontSize, setFontSizeState] = useState<'normal' | 'large' | 'larger'>('normal');
  const [reduceMotion, setReduceMotionState] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Load theme from localStorage on mount
  useEffect(() => {
    setMounted(true);

    // Check for saved theme preference
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as 'light' | 'dark' | 'midnight' | null;
    const savedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY);

    if (savedTheme && ['light', 'dark', 'midnight'].includes(savedTheme)) {
      setMode(savedTheme);
    } else {
      // Check system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }

    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        if (settings.isHighContrast !== undefined) setIsHighContrast(settings.isHighContrast);
        if (settings.fontSize) setFontSizeState(settings.fontSize);
        if (settings.reduceMotion !== undefined) setReduceMotionState(settings.reduceMotion);
      } catch (e) {
        console.warn('Failed to parse theme settings:', e);
      }
    }
  }, []);

  // Apply theme class to document
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;

    // Remove all theme classes first
    root.classList.remove('dark', 'midnight');

    // Apply the appropriate theme class(es)
    if (mode === 'dark') {
      root.classList.add('dark');
    } else if (mode === 'midnight') {
      // Midnight adds BOTH classes: 'dark' for dark: variants, 'midnight' for custom overrides
      root.classList.add('dark', 'midnight');
    }
    // 'light' mode = no class (uses :root defaults)

    // Save to localStorage
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode, mounted]);

  // Save settings to localStorage
  useEffect(() => {
    if (!mounted) return;

    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify({
      isHighContrast,
      fontSize,
      reduceMotion,
    }));

    // Apply high contrast
    const root = document.documentElement;
    if (isHighContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Apply font size
    root.classList.remove('text-normal', 'text-large', 'text-larger');
    root.classList.add(`text-${fontSize}`);

    // Apply reduce motion
    if (reduceMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }
  }, [isHighContrast, fontSize, reduceMotion, mounted]);

  const toggleTheme = useCallback(() => {
    setMode(prev => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'midnight';
      return 'light';
    });
  }, []);

  const setThemeMode = useCallback((newMode: 'light' | 'dark' | 'midnight') => {
    setMode(newMode);
  }, []);

  const setHighContrast = useCallback((enabled: boolean) => {
    setIsHighContrast(enabled);
  }, []);

  const setFontSize = useCallback((size: 'normal' | 'large' | 'larger') => {
    setFontSizeState(size);
  }, []);

  const setReduceMotion = useCallback((enabled: boolean) => {
    setReduceMotionState(enabled);
  }, []);

  const setModuleColor = useCallback((module: string, color: string) => {
    // Store module-specific colors in localStorage
    const moduleColors = JSON.parse(localStorage.getItem('pi-app-module-colors') || '{}');
    moduleColors[module] = color;
    localStorage.setItem('pi-app-module-colors', JSON.stringify(moduleColors));

    // Apply as CSS variable
    document.documentElement.style.setProperty(`--module-${module}-color`, color);
  }, []);

  const resetModuleColors = useCallback(() => {
    localStorage.removeItem('pi-app-module-colors');
    // Remove all module color CSS variables
    const style = document.documentElement.style;
    const cssVars = Array.from(style).filter(prop => prop.startsWith('--module-'));
    cssVars.forEach(prop => style.removeProperty(prop));
  }, []);

  const value: ThemeContextType = {
    mode,
    toggleTheme,
    setThemeMode,
    setHighContrast,
    setFontSize,
    setReduceMotion,
    setModuleColor,
    resetModuleColors,
    isHighContrast,
    fontSize,
    reduceMotion,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeProvider;